import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Button,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Box,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Switch,
  FormGroup,
  Paper
} from '@mui/material';
import {
  LinkedIn as LinkedInIcon,
  CheckCircle,
  Error,
  Refresh,
  LinkOff,
  Settings,
  PostAdd,
  CheckBox,
  CheckBoxOutlineBlank,
  SelectAll,
  Deselect
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const LinkedInConnect = ({ companyId, onConnectionChange }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [pages, setPages] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openManageDialog, setOpenManageDialog] = useState(false);
  const [message, setMessage] = useState({ text: '', severity: 'info' });
  const [selectedPages, setSelectedPages] = useState([]);
  const [updatingSelections, setUpdatingSelections] = useState(false);

  // Fetch LinkedIn connection status and pages
  const fetchLinkedInStatus = async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/linkedin/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStatus(response.data);
      if (response.data.connected) {
        setPages(response.data.pages || []);
        // Initialize selected pages based on is_selected  //connected
        const selected = response.data.pages
          .filter(page => page.is_selected)
          .map(page => page.page_urn);
        setSelectedPages(selected);
      } else {
        setPages([]);
        setSelectedPages([]);
      }
      
      if (onConnectionChange) {
        onConnectionChange(response.data.connected);
      }
    } catch (error) {
      //console.error('Error fetching LinkedIn status:', error);
      setStatus({ connected: false });
      setPages([]);
      setSelectedPages([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch pages separately (for manage dialog)
  const fetchPages = async () => {
    if (!companyId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/linkedin/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPages(response.data.pages || []);
      const selected = response.data.pages
        .filter(page => page.is_selected)
        .map(page => page.page_urn);
      setSelectedPages(selected);
    } catch (error) {
      //console.error('Error fetching LinkedIn pages:', error);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchLinkedInStatus();
    }
  }, [companyId]);

  // Listen for messages from popup
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'LINKEDIN_CONNECTED') {
        
        if (event.data.success) {
          setMessage({
            text: `✅ LinkedIn connected successfully! Found ${event.data.pageCount} page(s).`,
            severity: 'success'
          });
          // Refresh status and pages
          setTimeout(() => {
            fetchLinkedInStatus();
            fetchPages();
          }, 1000);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Connect to LinkedIn
  const handleConnect = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/linkedin/auth`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Open LinkedIn authorization in new tab
      const linkedinTab = window.open(
        response.data.auth_url,
        '_blank'
      );
      
      // Monitor the tab
      if (linkedinTab) {
        const checkInterval = setInterval(async () => {
          try {
            await fetchLinkedInStatus();
            clearInterval(checkInterval);
          } catch (error) {
          }
        }, 2000);
      }

    } catch (error) {
      //console.error('Error initiating LinkedIn connection:', error);
      setMessage({
        text: 'Failed to initiate LinkedIn connection',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setOpenDialog(false);
    }
  };

  // Open in same window
  const handleConnectSameWindow = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/linkedin/auth`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      localStorage.setItem('linkedinOAuthInProgress', 'true');
      localStorage.setItem('linkedinCompanyId', companyId);

      window.location.href = response.data.auth_url;
      
    } catch (error) {
      //console.error('Error initiating LinkedIn connection:', error);
      setMessage({
        text: 'Failed to initiate LinkedIn connection',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  // Disconnect LinkedIn (all pages)
  const handleDisconnect = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/linkedin/disconnect`,
      {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({
        text: 'LinkedIn disconnected successfully from all pages',
        severity: 'success'
      });
      fetchLinkedInStatus();
    } catch (error) {
      //console.error('Error disconnecting LinkedIn:', error);
      setMessage({
        text: 'Failed to disconnect LinkedIn',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setOpenDialog(false);
    }
  };

  // Disconnect single page
  const handleDisconnectPage = async (pageUrn) => {
    try {
      setUpdatingSelections(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/linkedin/pages/disconnect`,
        {  page_urn: pageUrn },{
           headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setSelectedPages(prev => prev.filter(urn => urn !== pageUrn));
      setPages(prev => prev.map(page => 
        page.page_urn === pageUrn ? { ...page, is_selected: false } : page
      ));

      setMessage({
        text: 'Page disconnected successfully',
        severity: 'success'
      });
    } catch (error) {
      //console.error('Error disconnecting page:', error);
      setMessage({
        text: 'Failed to disconnect page',
        severity: 'error'
      });
    } finally {
      setUpdatingSelections(false);
    }
  };

  // Update page selections
  const handleUpdateSelections = async () => {
    try {
      setUpdatingSelections(true);
      const token = localStorage.getItem('token');
      
      const selections = pages.map(page => ({
        page_urn: page.page_urn,
        is_selected: selectedPages.includes(page.page_urn)
      }));

      await axios.put(
        `${API_URL}/api/linkedin/pages/selection`,
        {  selections },{
           headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({
        text: 'Page selections updated successfully',
        severity: 'success'
      });
      
      // Refresh data
      fetchLinkedInStatus();
      fetchPages();
      
    } catch (error) {
      //console.error('Error updating page selections:', error);
      setMessage({
        text: 'Failed to update page selections',
        severity: 'error'
      });
    } finally {
      setUpdatingSelections(false);
    }
  };

  // Select all pages
  const handleSelectAll = async () => {
    try {
      setUpdatingSelections(true);
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_URL}/api/linkedin/pages/bulk-selection`,
        {  select_all: true} ,{
           headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      const allPageUrns = pages.map(page => page.page_urn);
      setSelectedPages(allPageUrns);
      
      setMessage({
        text: 'All pages selected for posting',
        severity: 'success'
      });
      
    } catch (error) {
      //console.error('Error selecting all pages:', error);
      setMessage({
        text: 'Failed to select all pages',
        severity: 'error'
      });
    } finally {
      setUpdatingSelections(false);
    }
  };

  // Deselect all pages
  const handleDeselectAll = async () => {
    try {
      setUpdatingSelections(true);
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_URL}/api/linkedin/pages/bulk-selection`,
        { select_all: false} ,{
           headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setSelectedPages([]);
      
      setMessage({
        text: 'All pages deselected',
        severity: 'info'
      });
      
    } catch (error) {
      //console.error('Error deselecting all pages:', error);
      setMessage({
        text: 'Failed to deselect all pages',
        severity: 'error'
      });
    } finally {
      setUpdatingSelections(false);
    }
  };

  // Handle individual page checkbox toggle
  const handlePageToggle = (pageUrn) => {
    setSelectedPages(prev => {
      if (prev.includes(pageUrn)) {
        return prev.filter(urn => urn !== pageUrn);
      } else {
        return [...prev, pageUrn];
      }
    });
  };

  // Refresh token
  const handleRefresh = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/linkedin/reconnect`,{},
      
        { headers: { Authorization: `Bearer ${token}` ,
    
            'Content-Type': 'application/json',
          
      } }
      );

      if (response.data.reconnect_required) {
        setMessage({
          text: 'LinkedIn requires reconnection. Please connect again.',
          severity: 'warning'
        });
        setOpenDialog(true);
      }
    } catch (error) {
      //console.error('Error refreshing token:', error);
      setMessage({
        text: 'Failed to refresh token. Please reconnect.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if user returned from OAuth
  useEffect(() => {
    const checkOAuthReturn = () => {
      const oauthInProgress = localStorage.getItem('linkedinOAuthInProgress');
      const storedCompanyId = localStorage.getItem('linkedinCompanyId');
      
      if (oauthInProgress === 'true' && storedCompanyId === companyId) {
        localStorage.removeItem('linkedinOAuthInProgress');
        localStorage.removeItem('linkedinCompanyId');
        
        setTimeout(() => {
          fetchLinkedInStatus();
          setMessage({
            text: 'LinkedIn connection completed!',
            severity: 'info'
          });
        }, 1000);
      }
    };

    checkOAuthReturn();
  }, [companyId]);

  // Status chip component
  const getStatusChip = () => {
    if (!status || loading) {
      return <Chip label="Checking..." color="default" size="small" />;
    }

    if (!status.connected) {
      return <Chip label="Not Connected" color="error" size="small" icon={<Error />} />;
    }

    if (status.token_status === 'expired') {
      return (
        <Chip
          label="Token Expired"
          color="error"
          size="small"
          icon={<Error />}
          onDelete={handleRefresh}
          deleteIcon={<Refresh />}
        />
      );
    }

    if (status.token_status === 'expiring_soon') {
      return (
        <Chip
          label={`Expiring in ${Math.round(status.hours_until_expiry)}h`}
          color="warning"
          size="small"
          onDelete={handleRefresh}
          deleteIcon={<Refresh />}
        />
      );
    }

    return (
      <Chip
        label={`Connected (${status.selected_pages}/${status.total_pages} pages)`}
        color="success"
        size="small"
        icon={<CheckCircle />}
      />
    );
  };

  // Page list item component
  const PageListItem = ({ page }) => {
    const isSelected = selectedPages.includes(page.page_urn);
    const isActive = page.is_active;
    
    return (
      <ListItem
        key={page.page_urn}
        sx={{
          border: '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
          borderRadius: 1,
          mb: 1,
          bgcolor: isSelected ? 'primary.50' : 'background.paper'
        }}
      >
        <ListItemIcon>
          <Checkbox
            edge="start"
            checked={isSelected}
            onChange={() => handlePageToggle(page.page_urn)}
            disabled={!isActive || updatingSelections}
          />
        </ListItemIcon>
        <ListItemText
          primary={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" fontWeight="medium">
                {page.page_name}
              </Typography>
              {!isActive && (
                <Chip label="Inactive" color="error" size="small" />
              )}
              {page.last_posted_at && (
                <Typography variant="caption" color="text.secondary">
                  Last posted: {new Date(page.last_posted_at).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          }
          secondary={
            <Typography variant="caption" color="text.secondary">
              {page.page_urn}
            </Typography>
          }
        />
        <Box>
          <Tooltip title={isSelected ? "Disconnect this page" : "Connect this page"}>
            <IconButton
              size="small"
              onClick={() => isSelected ? handleDisconnectPage(page.page_urn) : handlePageToggle(page.page_urn)}
              disabled={updatingSelections}
            >
              {isSelected ? <LinkOff fontSize="small" /> : <PostAdd fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </ListItem>
    );
  };

  return (
    <>
      {/* Main Connection Card */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <LinkedInIcon color="primary" />
              <Typography variant="h6">LinkedIn Integration</Typography>
              {getStatusChip()}
            </Box>
            
            <Box display="flex" gap={1}>
              {status?.connected && (
                <>
                  <Tooltip title="Manage Pages">
                    <IconButton 
                      onClick={() => {
                        fetchPages();
                        setOpenManageDialog(true);
                      }} 
                      size="small" 
                      color="primary"
                    >
                      <Settings />
                    </IconButton>
                  </Tooltip>
                  
                  {status.token_status === 'expiring_soon' && (
                    <Tooltip title="Refresh Token">
                      <IconButton onClick={handleRefresh} size="small" color="warning" disabled={loading}>
                        <Refresh />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
              
              {status?.connected ? (
                <Tooltip title="Disconnect All Pages">
                  <IconButton onClick={() => setOpenDialog(true)} size="small" color="error" disabled={loading}>
                    <LinkOff />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<LinkedInIcon />}
                  onClick={handleConnectSameWindow}
                  disabled={loading}
                  size="small"
                >
                  Connect
                </Button>
              )}
            </Box>
          </Box>

          {status?.connected && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                <strong>Pages:</strong> {status.selected_pages} selected of {status.total_pages} total
              </Typography>
              {pages.slice(0, 2).map(page => (
                <Chip
                  key={page.page_urn}
                  label={`${page.page_name} ${page.is_selected ? '✓' : '✗'}`}
                  size="small"
                  color={page.is_selected ? "success" : "default"}
                  variant="outlined"
                  sx={{ mr: 1, mt: 1 }}
                />
              ))}
              {pages.length > 2 && (
                <Chip
                  label={`+${pages.length - 2} more`}
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                />
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Expires:</strong> {new Date(status.expires_at).toLocaleDateString()}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Connection/Disconnection Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {status?.connected ? 'Disconnect LinkedIn' : 'Connect LinkedIn'}
        </DialogTitle>
        <DialogContent>
          {status?.connected ? (
            <Box>
              <Typography paragraph>
                Are you sure you want to disconnect ALL LinkedIn pages?
              </Typography>
              <Typography variant="body2" color="error">
                ⚠️ This will disconnect {status.total_pages} page(s) and stop all automatic job postings.
              </Typography>
              {pages.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2">Pages to disconnect:</Typography>
                  <List dense>
                    {pages.slice(0, 5).map(page => (
                      <ListItem key={page.page_urn} dense>
                        <ListItemText primary={page.page_name} />
                      </ListItem>
                    ))}
                    {pages.length > 5 && (
                      <ListItem dense>
                        <ListItemText 
                          primary={`... and ${pages.length - 5} more page(s)`} 
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              )}
            </Box>
          ) : (
            <Box>
              <Typography paragraph>
                Connect your LinkedIn Company Page(s) to automatically post job openings.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You'll be redirected to LinkedIn to grant permissions. After authorizing, 
                you'll be brought back to this page automatically.
              </Typography>
              <Box mt={2}>
                <Typography variant="body2" color="info.main">
                  💡 <strong>Tip:</strong> Make sure you have Admin access to your Company's LinkedIn Page(s)
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          {status?.connected ? (
            <Button
              onClick={handleDisconnect}
              color="error"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <LinkOff />}
            >
              {loading ? 'Disconnecting...' : 'Disconnect All'}
            </Button>
          ) : (
            <Button
              onClick={handleConnectSameWindow}
              color="primary"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <LinkedInIcon />}
            >
              {loading ? 'Connecting...' : 'Connect to LinkedIn'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Manage Pages Dialog */}
      <Dialog 
        open={openManageDialog} 
        onClose={() => setOpenManageDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Manage LinkedIn Pages</Typography>
            <Box display="flex" gap={1}>
              <Tooltip title="Select All">
                <IconButton 
                  size="small" 
                  onClick={handleSelectAll}
                  disabled={updatingSelections || selectedPages.length === pages.length}
                >
                  <SelectAll />
                </IconButton>
              </Tooltip>
              <Tooltip title="Deselect All">
                <IconButton 
                  size="small" 
                  onClick={handleDeselectAll}
                  disabled={updatingSelections || selectedPages.length === 0}
                >
                  <Deselect />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {pages.length === 0 ? (
            <Box textAlign="center" py={4}>
              <LinkedInIcon color="disabled" sx={{ fontSize: 48, mb: 2 }} />
              <Typography color="text.secondary">
                No LinkedIn pages connected. Please connect to LinkedIn first.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" paragraph>
                Select which pages should receive job postings. Pages not selected will remain connected but won't receive posts.
              </Typography>
              
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {pages.map(page => (
                  <PageListItem key={page.page_urn} page={page} />
                ))}
              </List>
              
              <Box mt={2} p={2} bgcolor="info.50" borderRadius={1}>
                <Typography variant="body2">
                  <strong>📝 Note:</strong> All pages share the same connection. 
                  Deselecting a page only stops posts to that page, 
                  it doesn't disconnect the page from your account.
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenManageDialog(false)}>
            Cancel
          </Button>
          {pages.length > 0 && (
            <Button
              onClick={handleUpdateSelections}
              variant="contained"
              disabled={updatingSelections}
              startIcon={updatingSelections ? <CircularProgress size={20} /> : <CheckCircle />}
            >
              {updatingSelections ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Message Alert */}
      {message.text && (
        <Alert
          severity={message.severity}
          onClose={() => setMessage({ text: '', severity: 'info' })}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}
    </>
  );
};

export default LinkedInConnect;